// src/pages/ClientAdvisorSpace.jsx
// §8bis « Ce que voit le client relié » : espace de collaboration SIMPLIFIÉ (lecture).
// Ne reproduit PAS le dashboard Advisor. Montre le/les conseiller(s) qui gèrent un
// dossier pour le client, et UNIQUEMENT le contenu client-partagé (missions
// client_visible) — jamais les notes internes/marges (§8ter). Données via la RPC
// SECURITY DEFINER get_client_advisor_space. i18n FR/EN/DE.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { missionTypeLabel, missionStatusLabel } from "../config/advisorMissions";
import { PERMISSION_CATEGORIES, mergeGrants, permissionLabel } from "../config/dossierPermissions";
import { UserCircle, Package, Image, Briefcase, ArrowLeft, ShieldCheck, Plus, Paperclip, Send, FileText, ExternalLink } from "lucide-react";

const COPY = {
  fr: {
    title: "Mon conseiller", titlePlural: "Mes conseillers", back: "Retour",
    subtitle: "Retrouvez ici le suivi partagé par votre Art Advisor. Vous gardez le contrôle : vous pouvez à tout moment gérer ou révoquer les accès depuis votre compte.",
    managedBy: "Suivi par", collections: "collections", artworks: "œuvres",
    missions: "Suivi partagé", noMissions: "Aucun suivi partagé pour le moment.",
    empty: "Aucun conseiller n’est relié à votre compte pour l’instant.",
    loading: "Chargement…",
    permissions: "Accès que vous accordez", permsHint: "Vous décidez de ce que votre conseiller peut consulter. Décochez pour révoker à tout moment.",
    contribTitle: "Compléter mon dossier", contribHint: "Ajoutez une information ou déposez un document pour votre conseiller.",
    add: "Ajouter", cancel: "Annuler", titleField: "Titre", notePh: "Information à transmettre…", file: "Document (facultatif)",
    submit: "Envoyer", submitting: "Envoi…", myContribs: "Mes contributions", noContribs: "Aucune contribution pour l’instant.",
    open: "Ouvrir", cStatus: { submitted: "En attente", validated: "Validé", rejected: "Refusé" },
    toFulfill: "Demandes de votre conseiller", respond: "Répondre",
    tfAction: "Confirmer l’envoi", tfTitle: "Confirmer le transfert de la collection",
    tfHint: "Vous allez transférer la propriété de cette collection. Saisissez l’adresse e-mail du nouveau propriétaire, puis ressaisissez-la pour confirmer : elle recevra le lien d’acceptation.",
    tfEmail: "E-mail du nouveau propriétaire", tfEmail2: "Confirmez l’e-mail", tfMismatch: "Les deux adresses ne correspondent pas.",
    tfHistory: "Transmettre l’historique des modifications au nouveau propriétaire", tfSend: "Envoyer le transfert", tfSending: "Envoi…",
    tfSent: "Transfert envoyé. La collection change de propriétaire dès que le destinataire accepte.",
    qView: "Voir le devis", qTitle: "Devis de votre conseiller", qHT: "Total HT", qTax: "TVA", qTTC: "Total TTC", qDeposit: "Acompte",
    qValid: "Valable jusqu’au", qPayment: "Paiement", qDelivery: "Délai", qNotes: "Notes", qExcl: "Exclusions", qQty: "Qté",
    qNote: "Commentaire (facultatif)", qAccept: "Accepter le devis", qRefuse: "Refuser", qBusy: "Envoi…", qClose: "Fermer",
    qAccepted: "Devis accepté.", qRefused: "Devis refusé.",
    qErr: { not_pending: "Ce devis n’est plus en attente de réponse.", expired: "Ce devis a expiré.", not_found: "Devis introuvable." },
  },
  en: {
    title: "My advisor", titlePlural: "My advisors", back: "Back",
    subtitle: "Here is the follow-up shared by your Art Advisor. You stay in control: you can manage or revoke access from your account at any time.",
    managedBy: "Managed by", collections: "collections", artworks: "artworks",
    missions: "Shared follow-up", noMissions: "Nothing shared yet.",
    empty: "No advisor is linked to your account yet.",
    loading: "Loading…",
    permissions: "Access you grant", permsHint: "You decide what your advisor can view. Uncheck to revoke at any time.",
    contribTitle: "Complete my file", contribHint: "Add information or upload a document for your advisor.",
    add: "Add", cancel: "Cancel", titleField: "Title", notePh: "Information to share…", file: "Document (optional)",
    submit: "Send", submitting: "Sending…", myContribs: "My contributions", noContribs: "No contribution yet.",
    open: "Open", cStatus: { submitted: "Pending", validated: "Validated", rejected: "Rejected" },
    toFulfill: "Requests from your advisor", respond: "Respond",
    tfAction: "Confirm sending", tfTitle: "Confirm the collection transfer",
    tfHint: "You are about to transfer ownership of this collection. Enter the new owner’s e-mail, then enter it again to confirm: they will receive the acceptance link.",
    tfEmail: "New owner’s e-mail", tfEmail2: "Confirm e-mail", tfMismatch: "The two addresses do not match.",
    tfHistory: "Pass the edit history on to the new owner", tfSend: "Send transfer", tfSending: "Sending…",
    tfSent: "Transfer sent. The collection changes owner as soon as the recipient accepts.",
    qView: "View quote", qTitle: "Your advisor’s quote", qHT: "Total excl. tax", qTax: "VAT", qTTC: "Total incl. tax", qDeposit: "Deposit",
    qValid: "Valid until", qPayment: "Payment", qDelivery: "Lead time", qNotes: "Notes", qExcl: "Exclusions", qQty: "Qty",
    qNote: "Comment (optional)", qAccept: "Accept quote", qRefuse: "Decline", qBusy: "Sending…", qClose: "Close",
    qAccepted: "Quote accepted.", qRefused: "Quote declined.",
    qErr: { not_pending: "This quote is no longer awaiting a response.", expired: "This quote has expired.", not_found: "Quote not found." },
  },
  de: {
    title: "Mein Advisor", titlePlural: "Meine Advisors", back: "Zurück",
    subtitle: "Hier finden Sie die von Ihrem Art Advisor geteilte Nachverfolgung. Sie behalten die Kontrolle: Zugriffe können Sie jederzeit in Ihrem Konto verwalten oder widerrufen.",
    managedBy: "Betreut von", collections: "Sammlungen", artworks: "Werke",
    missions: "Geteilte Nachverfolgung", noMissions: "Noch nichts geteilt.",
    empty: "Es ist noch kein Advisor mit Ihrem Konto verknüpft.",
    loading: "Laden…",
    permissions: "Von Ihnen gewährte Zugriffe", permsHint: "Sie entscheiden, was Ihr Advisor einsehen darf. Zum Widerruf abwählen.",
    contribTitle: "Meine Akte ergänzen", contribHint: "Fügen Sie eine Information hinzu oder laden Sie ein Dokument für Ihren Advisor hoch.",
    add: "Hinzufügen", cancel: "Abbrechen", titleField: "Titel", notePh: "Zu übermittelnde Information…", file: "Dokument (optional)",
    submit: "Senden", submitting: "Senden…", myContribs: "Meine Beiträge", noContribs: "Noch keine Beiträge.",
    open: "Öffnen", cStatus: { submitted: "Ausstehend", validated: "Bestätigt", rejected: "Abgelehnt" },
    toFulfill: "Anfragen Ihres Advisors", respond: "Antworten",
    tfAction: "Versand bestätigen", tfTitle: "Übertragung der Sammlung bestätigen",
    tfHint: "Sie übertragen das Eigentum an dieser Sammlung. Geben Sie die E-Mail-Adresse des neuen Eigentümers ein und wiederholen Sie sie zur Bestätigung: Er erhält den Annahmelink.",
    tfEmail: "E-Mail des neuen Eigentümers", tfEmail2: "E-Mail bestätigen", tfMismatch: "Die beiden Adressen stimmen nicht überein.",
    tfHistory: "Änderungshistorie an den neuen Eigentümer weitergeben", tfSend: "Übertragung senden", tfSending: "Senden…",
    tfSent: "Übertragung gesendet. Die Sammlung wechselt den Eigentümer, sobald der Empfänger annimmt.",
    qView: "Angebot ansehen", qTitle: "Angebot Ihres Advisors", qHT: "Gesamt netto", qTax: "MwSt.", qTTC: "Gesamt brutto", qDeposit: "Anzahlung",
    qValid: "Gültig bis", qPayment: "Zahlung", qDelivery: "Lieferzeit", qNotes: "Hinweise", qExcl: "Ausschlüsse", qQty: "Menge",
    qNote: "Kommentar (optional)", qAccept: "Angebot annehmen", qRefuse: "Ablehnen", qBusy: "Senden…", qClose: "Schließen",
    qAccepted: "Angebot angenommen.", qRefused: "Angebot abgelehnt.",
    qErr: { not_pending: "Dieses Angebot wartet nicht mehr auf eine Antwort.", expired: "Dieses Angebot ist abgelaufen.", not_found: "Angebot nicht gefunden." },
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

const STATUS_TONE = {
  in_progress: { bg: "#fbf6ea", c: "#8a6a2f" }, waiting_client: { bg: "#fbf6ea", c: "#8a6a2f" },
  waiting_third_party: { bg: "#fbf6ea", c: "#8a6a2f" }, ready_to_deliver: { bg: "#eef1f6", c: "#3a4a6b" },
  delivered: { bg: "#f1f6f1", c: "#2c5f2d" }, closed: { bg: "#f1f6f1", c: "#2c5f2d" },
  accepted: { bg: "#eef1f6", c: "#3a4a6b" }, proposed: { bg: "#eef1f6", c: "#3a4a6b" },
  draft: { bg: "#f5f5f4", c: "#57534e" }, cancelled: { bg: "#f5f5f4", c: "#57534e" },
};

export default function ClientAdvisorSpace() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);
  const locale = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2) === "de" ? "de-DE" : String(i18n.resolvedLanguage || "fr").slice(0, 2) === "en" ? "en-GB" : "fr-FR";

  const [loading, setLoading] = useState(true);
  const [dossiers, setDossiers] = useState([]);
  const [contribs, setContribs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [contribOpen, setContribOpen] = useState(null);   // entity_id | null
  const [cForm, setCForm] = useState({ title: "", note: "", file: null, request_id: null });
  const [cBusy, setCBusy] = useState(false);
  const [tf, setTf] = useState(null); // { mission, email, email2, keepHistory, busy, error, sent }
  const [qr, setQr] = useState(null); // { mission, note, busy, error, done }

  const money = (v, cur) => new Intl.NumberFormat(locale, { style: "currency", currency: (cur || "EUR").trim() || "EUR" }).format(Number(v) || 0);


  const submitTransfer = useCallback(async () => {
    if (!tf) return;
    const email = tf.email.trim().toLowerCase();
    if (!email || email !== tf.email2.trim().toLowerCase()) { setTf((p) => ({ ...p, error: "mismatch" })); return; }
    setTf((p) => ({ ...p, busy: true, error: null }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/initiate-collection-transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          to_email: email,
          collection_ids: [tf.mission.collection_id],
          mission_id: tf.mission.id,
          keep_history: tf.keepHistory,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Erreur");
      setTf((p) => ({ ...p, busy: false, sent: true }));
    } catch (e) {
      setTf((p) => ({ ...p, busy: false, error: e.message }));
    }
  }, [tf]);

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    const [{ data, error }, { data: cData }, { data: rData }] = await Promise.all([
      supabase.rpc("get_client_advisor_space"),
      supabase.from("advisor_client_contributions").select("*").eq("submitted_by", user.id).order("created_at", { ascending: false }),
      supabase.from("advisor_client_requests").select("*").eq("status", "requested").order("created_at", { ascending: false }),
    ]);
    if (!error && data?.ok) setDossiers(Array.isArray(data.dossiers) ? data.dossiers : []);
    else setDossiers([]);
    setContribs(Array.isArray(cData) ? cData : []);
    setReqs(Array.isArray(rData) ? rData : []);
    setLoading(false);
  }, [user?.id]);

  const respondQuote = useCallback(async (accept) => {
    if (!qr) return;
    setQr((p) => ({ ...p, busy: true, error: null }));
    const { data, error } = await supabase.rpc("client_respond_to_quote", {
      p_quote_id: qr.mission.quote.id, p_accept: accept, p_note: qr.note || null,
    });
    if (error || data?.error) {
      setQr((p) => ({ ...p, busy: false, error: data?.error || error.message }));
      return;
    }
    setQr((p) => ({ ...p, busy: false, done: data.status }));
    await load();
  }, [qr, load]);

  const submitContrib = useCallback(async (entityId, advisorMissing) => {
    if (!user?.id) return;
    const title = cForm.title.trim();
    const note = cForm.note.trim();
    if (!title && !note && !cForm.file) return;
    setCBusy(true);
    try {
      // advisor_id du dossier requis par la RLS d'insertion
      const { data: ent } = await supabase.from("advisor_client_entities").select("primary_advisor_id").eq("id", entityId).maybeSingle();
      const advisorId = ent?.primary_advisor_id;
      if (!advisorId) throw new Error("dossier");
      let storage_path = null, file_name = null, kind = "info";
      if (cForm.file) {
        const safe = cForm.file.name.replace(/[^\w.-]+/g, "_");
        const path = `${entityId}/${(window.crypto?.randomUUID?.() || Date.now())}-${safe}`;
        const { error: upErr } = await supabase.storage.from("client-contributions").upload(path, cForm.file, { upsert: false });
        if (upErr) throw upErr;
        storage_path = path; file_name = cForm.file.name; kind = "document";
      }
      const { error } = await supabase.from("advisor_client_contributions").insert({
        client_entity_id: entityId, advisor_id: advisorId, submitted_by: user.id,
        kind, title: title || file_name || null, note: note || null, storage_path, file_name,
        request_id: cForm.request_id || null,
      });
      if (error) throw error;
      setCForm({ title: "", note: "", file: null, request_id: null });
      setContribOpen(null);
      await load();
    } catch (e) {
      console.warn("[ClientAdvisorSpace] submitContrib", e);
      alert(e?.message || "");
    } finally {
      setCBusy(false);
    }
  }, [user?.id, cForm, load]);

  const openContribFile = useCallback(async (path) => {
    if (!path) return;
    const { data } = await supabase.storage.from("client-contributions").createSignedUrl(path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  }, []);

  useEffect(() => { load(); }, [load]);

  // Lien profond depuis le tableau de bord : /mon-conseiller?mission=<id>&open=quote|transfer
  useEffect(() => {
    if (!dossiers.length) return;
    const params = new URLSearchParams(window.location.search);
    const missionId = params.get("mission");
    const what = params.get("open");
    if (!missionId || !what) return;
    const m = dossiers.flatMap((d) => d.missions || []).find((x) => x.id === missionId);
    if (!m) return;
    if (what === "quote" && m.quote?.status === "sent") setQr({ mission: m, note: "" });
    if (what === "transfer") setTf({ mission: m, email: "", email2: "", keepHistory: true });
    window.history.replaceState(null, "", window.location.pathname);
  }, [dossiers]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : null);

  const togglePerm = useCallback(async (entityId, key) => {
    let nextGrants = null;
    setDossiers((prev) => prev.map((d) => {
      if (d.entity_id !== entityId) return d;
      const cur = mergeGrants(d.granted_permissions);
      nextGrants = { ...cur, [key]: !cur[key] };
      return { ...d, granted_permissions: nextGrants };
    }));
    if (!nextGrants) return;
    const { data, error } = await supabase.rpc("set_dossier_permissions", { p_entity_id: entityId, p_permissions: nextGrants });
    if (error || data?.error) load(); // resync si échec
  }, [load]);

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f4", padding: "32px 20px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button onClick={() => navigate("/dashboard")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#6b6b6b", fontSize: 13, cursor: "pointer", marginBottom: 16 }}>
          <ArrowLeft size={16} /> {tx.back}
        </button>

        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>{dossiers.length > 1 ? tx.titlePlural : tx.title}</h1>
        <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "8px 0 24px", maxWidth: 640 }}>{tx.subtitle}</p>

        {loading ? (
          <div style={{ color: "#9ca3af", fontSize: 14 }}>{tx.loading}</div>
        ) : dossiers.length === 0 ? (
          <div style={{ padding: 20, background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, color: "#6b6b6b", fontSize: 14 }}>{tx.empty}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {dossiers.map((d) => (
              <div key={d.entity_id} style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 24, boxShadow: "0 1px 3px rgba(26,26,26,0.04)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 4 }}>{tx.managedBy}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 34, height: 34, borderRadius: "50%", background: "#faf6ea", color: "#8b7355", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><UserCircle size={20} /></span>
                      <div>
                        <div style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a" }}>{d.advisor_name || "Art Advisor"}</div>
                        <div style={{ fontSize: 12.5, color: "#9ca3af" }}>{d.display_name}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#6b6b6b" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Package size={15} /> {d.collections_count || 0} {tx.collections}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Image size={15} /> {d.artworks_count || 0} {tx.artworks}</span>
                  </div>
                </div>

                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <Briefcase size={13} /> {tx.missions}
                  </div>
                  {(!d.missions || d.missions.length === 0) ? (
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>{tx.noMissions}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {d.missions.map((m) => {
                        const tone = STATUS_TONE[m.status] || STATUS_TONE.draft;
                        const due = fmtDate(m.due_at);
                        const canConfirmTransfer = m.mission_type === "collection_transfer" && m.collection_id && m.quote_accepted
                          && ["accepted", "in_progress", "waiting_client", "ready_to_deliver"].includes(m.status);
                        const tfOpen = tf?.mission?.id === m.id;
                        const quoteSent = m.quote?.status === "sent";
                        const qOpen = qr?.mission?.id === m.id;
                        return (
                          <React.Fragment key={m.id}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", background: "#fafaf8", border: "1px solid #f2efe9", borderRadius: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 14, color: "#1a1a1a", fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.title}</div>
                              <div style={{ fontSize: 12, color: "#9ca3af" }}>{missionTypeLabel(m.mission_type, i18n.resolvedLanguage || i18n.language)}{due ? ` · ${due}` : ""}</div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                              {quoteSent && !qOpen && (
                                <button type="button" onClick={() => setQr({ mission: m, note: "" })}
                                  style={{ padding: "6px 12px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                                  {tx.qView}
                                </button>
                              )}
                              {canConfirmTransfer && !tfOpen && (
                                <button type="button" onClick={() => setTf({ mission: m, email: "", email2: "", keepHistory: true })}
                                  style={{ padding: "6px 12px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                                  {tx.tfAction}
                                </button>
                              )}
                              <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: tone.bg, color: tone.c }}>{missionStatusLabel(m.status, i18n.resolvedLanguage || i18n.language)}</span>
                            </div>
                          </div>
                          {qOpen && (() => { const q = m.quote; return (
                            <div style={{ padding: 14, background: "#faf8f4", border: "1px solid #e5e3df", borderRadius: 8, display: "grid", gap: 10 }}>
                              <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#1a1a1a" }}>{tx.qTitle}{q.object ? ` — ${q.object}` : ""}</div>
                              <div style={{ display: "grid", gap: 4 }}>
                                {(q.lines || []).map((l, i) => (
                                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, color: "#1a1a1a" }}>
                                    <span>{l.label}{Number(l.qty) !== 1 ? ` × ${l.qty}` : ""}{l.detail ? <span style={{ color: "#9ca3af" }}> · {l.detail}</span> : null}</span>
                                    <span style={{ flexShrink: 0 }}>{money((Number(l.qty) || 0) * (Number(l.unit_price_ex_tax) || 0) * (1 - (Number(l.discount_pct) || 0) / 100), q.currency)}</span>
                                  </div>
                                ))}
                              </div>
                              <div style={{ borderTop: "1px solid #e5e3df", paddingTop: 8, display: "grid", gap: 3, fontSize: 13, color: "#6b6b6b" }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>{tx.qHT}</span><span>{money(q.subtotal_ex_tax, q.currency)}</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>{tx.qTax}</span><span>{money(q.tax_amount, q.currency)}</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between", color: "#1a1a1a", fontWeight: 600 }}><span>{tx.qTTC}</span><span>{money(q.total_inc_tax, q.currency)}</span></div>
                                {Number(q.deposit_amount) > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{tx.qDeposit}</span><span>{money(q.deposit_amount, q.currency)}</span></div>}
                              </div>
                              <div style={{ fontSize: 12.5, color: "#6b6b6b", display: "grid", gap: 2 }}>
                                {q.valid_until && <div>{tx.qValid} {fmtDate(q.valid_until)}</div>}
                                {q.payment_terms && <div>{tx.qPayment} : {q.payment_terms}</div>}
                                {q.delivery_terms && <div>{tx.qDelivery} : {q.delivery_terms}</div>}
                                {q.notes && <div>{tx.qNotes} : {q.notes}</div>}
                                {q.exclusions && <div>{tx.qExcl} : {q.exclusions}</div>}
                              </div>
                              {qr.done ? (
                                <div style={{ fontSize: 13.5, color: qr.done === "accepted" ? "#2c5f2d" : "#8f3a44" }}>{qr.done === "accepted" ? tx.qAccepted : tx.qRefused}</div>
                              ) : (
                                <>
                                  <textarea value={qr.note} onChange={(e) => setQr((p) => ({ ...p, note: e.target.value }))} placeholder={tx.qNote} rows={2}
                                    style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none", resize: "vertical" }} />
                                  {qr.error && <div style={{ fontSize: 12.5, color: "#8f3a44" }}>{tx.qErr[qr.error] || qr.error}</div>}
                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <button type="button" disabled={qr.busy} onClick={() => respondQuote(true)}
                                      style={{ padding: "9px 16px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: qr.busy ? 0.5 : 1 }}>
                                      {qr.busy ? tx.qBusy : tx.qAccept}
                                    </button>
                                    <button type="button" disabled={qr.busy} onClick={() => respondQuote(false)}
                                      style={{ padding: "9px 16px", background: "#fff", color: "#8f3a44", border: "1px solid #e5c9ce", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: qr.busy ? 0.5 : 1 }}>
                                      {tx.qRefuse}
                                    </button>
                                  </div>
                                </>
                              )}
                              <div><button type="button" onClick={() => setQr(null)} style={{ background: "transparent", border: "none", color: "#6b6b6b", fontSize: 12.5, cursor: "pointer", padding: 0 }}>{tx.qClose}</button></div>
                            </div>
                          ); })()}
                          {tfOpen && (
                            <div style={{ padding: 14, background: "#faf8f4", border: "1px solid #e5e3df", borderRadius: 8, display: "grid", gap: 10 }}>
                              <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#1a1a1a" }}>{tx.tfTitle}</div>
                              {tf.sent ? (
                                <div style={{ fontSize: 13.5, color: "#2c5f2d" }}>{tx.tfSent}</div>
                              ) : (
                                <>
                                  <div style={{ fontSize: 12.5, color: "#6b6b6b", lineHeight: 1.5 }}>{tx.tfHint}</div>
                                  <input type="email" value={tf.email} onChange={(e) => setTf((p) => ({ ...p, email: e.target.value, error: null }))} placeholder={tx.tfEmail}
                                    style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none" }} />
                                  <input type="email" value={tf.email2} onChange={(e) => setTf((p) => ({ ...p, email2: e.target.value, error: null }))} placeholder={tx.tfEmail2}
                                    onPaste={(e) => e.preventDefault()}
                                    style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none" }} />
                                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1a1a1a", cursor: "pointer" }}>
                                    <input type="checkbox" checked={tf.keepHistory} onChange={() => setTf((p) => ({ ...p, keepHistory: !p.keepHistory }))} style={{ accentColor: "#8b7355" }} />
                                    {tx.tfHistory}
                                  </label>
                                  {tf.error && <div style={{ fontSize: 12.5, color: "#8f3a44" }}>{tf.error === "mismatch" ? tx.tfMismatch : tf.error}</div>}
                                  <div style={{ display: "flex", gap: 8 }}>
                                    <button type="button" disabled={tf.busy || !tf.email || !tf.email2} onClick={submitTransfer}
                                      style={{ padding: "9px 16px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (tf.busy || !tf.email || !tf.email2) ? 0.5 : 1 }}>
                                      {tf.busy ? tx.tfSending : tx.tfSend}
                                    </button>
                                    <button type="button" onClick={() => setTf(null)}
                                      style={{ padding: "9px 16px", background: "#fff", color: "#555", border: "1px solid #e5e3df", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                      {tx.cancel}
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Permissions : le client contrôle et peut révoquer (§8ter) */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    <ShieldCheck size={13} /> {tx.permissions}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 10, lineHeight: 1.5 }}>{tx.permsHint}</div>
                  {(() => { const g = mergeGrants(d.granted_permissions); return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 6 }}>
                      {PERMISSION_CATEGORIES.map((cat) => (
                        <label key={cat.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 8, cursor: "pointer", background: g[cat.key] ? "#faf6ea" : "#fff" }}>
                          <input type="checkbox" checked={!!g[cat.key]} onChange={() => togglePerm(d.entity_id, cat.key)} style={{ width: 15, height: 15, accentColor: "#8b7355", flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: "#1a1a1a" }}>{permissionLabel(cat.key, i18n.resolvedLanguage || i18n.language)}</span>
                        </label>
                      ))}
                    </div>
                  ); })()}
                </div>

                {/* Demandes de l'Advisor à fournir (§8bis) */}
                {(() => { const open = reqs.filter((r) => r.client_entity_id === d.entity_id); return open.length === 0 ? null : (
                  <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8a6a2f", fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <Paperclip size={13} /> {tx.toFulfill} · {open.length}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {open.map((r) => (
                        <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: "#fbf6ea", border: "1px solid #ecdcae", borderRadius: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title}</div>
                            {r.description && <div style={{ fontSize: 12.5, color: "#8a6a2f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.description}</div>}
                          </div>
                          <button type="button" onClick={() => { setContribOpen(d.entity_id); setCForm({ title: r.title, note: "", file: null, request_id: r.id }); }}
                            style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                            <Send size={14} /> {tx.respond}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ); })()}

                {/* Contributions : le client complète son dossier (§8bis) */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                        <Paperclip size={13} /> {tx.contribTitle}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#9ca3af", marginTop: 2 }}>{tx.contribHint}</div>
                    </div>
                    <button type="button" onClick={() => { setContribOpen(contribOpen === d.entity_id ? null : d.entity_id); setCForm({ title: "", note: "", file: null, request_id: null }); }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", background: contribOpen === d.entity_id ? "#fff" : "#1a1a1a", color: contribOpen === d.entity_id ? "#1a1a1a" : "#fff", border: contribOpen === d.entity_id ? "1px solid #e5e3df" : "none", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
                      <Plus size={14} /> {contribOpen === d.entity_id ? tx.cancel : tx.add}
                    </button>
                  </div>

                  {contribOpen === d.entity_id && (
                    <div style={{ padding: 14, background: "#faf8f4", border: "1px solid #e5e3df", borderRadius: 8, marginBottom: 12, display: "grid", gap: 10 }}>
                      <input value={cForm.title} onChange={(e) => setCForm((p) => ({ ...p, title: e.target.value }))} placeholder={tx.titleField}
                        style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none" }} />
                      <textarea value={cForm.note} onChange={(e) => setCForm((p) => ({ ...p, note: e.target.value }))} placeholder={tx.notePh} rows={2}
                        style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none", resize: "vertical" }} />
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#6b6b6b", cursor: "pointer" }}>
                        <Paperclip size={15} /> {cForm.file ? cForm.file.name : tx.file}
                        <input type="file" onChange={(e) => setCForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} style={{ display: "none" }} />
                      </label>
                      <div>
                        <button type="button" disabled={cBusy} onClick={() => submitContrib(d.entity_id)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: cBusy ? "default" : "pointer", opacity: cBusy ? 0.6 : 1 }}>
                          <Send size={15} /> {cBusy ? tx.submitting : tx.submit}
                        </button>
                      </div>
                    </div>
                  )}

                  {(() => { const mine = contribs.filter((c) => c.client_entity_id === d.entity_id); return mine.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: "#9ca3af" }}>{tx.noContribs}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {mine.map((c) => {
                        const tone = c.status === "validated" ? { bg: "#f1f6f1", c: "#2c5f2d" } : c.status === "rejected" ? { bg: "#f8eef0", c: "#8f3a44" } : { bg: "#fbf6ea", c: "#8a6a2f" };
                        return (
                          <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 11px", background: "#fff", border: "1px solid #e5e3df", borderRadius: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13.5, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {c.kind === "document" && <FileText size={13} style={{ flexShrink: 0, color: "#8b7355" }} />}{c.title || c.file_name || "—"}
                              </div>
                              {c.note && <div style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.note}</div>}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                              {c.storage_path && (
                                <button type="button" onClick={() => openContribFile(c.storage_path)} title={tx.open}
                                  style={{ background: "transparent", border: "none", color: "#8b7355", cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                                  <ExternalLink size={14} />
                                </button>
                              )}
                              <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: tone.bg, color: tone.c }}>{tx.cStatus[c.status] || c.status}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ); })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
